<template>
  <b-modal
    id="add-or-update-modal"
    :visible="isAddActive"
    centered
    hide-footer
    hide-header
    no-close-on-esc
    no-close-on-backdrop
    size="lg"
    @hidden="resetForm"
    @hide="resetForm"
    @change="(val) => $emit('update:is-add-active', val)"
  >
    <template #default="{ hide }">
      <b-overlay :show="loading" class="px-2">
        <div class="d-flex justify-content-between align-items-center content-sidebar-header py-1" style="border-bottom: 1px solid #000000;">
          <h5 class="mb-0" style="font-weight: 400; font-size: 14px; line-height: 160%;">
            Add {{ modalTitle }}
          </h5>

          <feather-icon class="ml-1 cursor-pointer" icon="XIcon" size="16" @click="hide" />
        </div>

        <validation-observer
          #default="{ handleSubmit, invalid }"
          ref="update_modal_form"
        >
          <b-form class="my-1" @submit.prevent="handleSubmit(onCreateOrUpdate)" @reset.prevent="resetForm">
            <b-alert v-if="errorMessage" variant="danger" :show="true">
              <div class="alert-body font-small-2">
                <b-card-text class="mb-0">
                  {{ errorMessage }}
                </b-card-text>
              </div>
            </b-alert>
            
            <b-row>
              <b-col cols="12" md="10">
                <validation-provider #default="validationContext" name="Partner" rules="">
                  <b-form-group label-for="description">
                    <template #label>
                      <span>Partner<span style="color: tomato">*</span></span>
                    </template>
                    
                    <partner-select 
                      :value="partnerData"
                      @change="val => partnerData = val"
                    />

                    <b-form-invalid-feedback>
                      {{ validationContext.errors[0] }}
                    </b-form-invalid-feedback>
                  </b-form-group>
                </validation-provider>
              </b-col>

              <b-col cols="12" md="10">
                <validation-provider #default="validationContext" name="Patient" rules="">
                  <b-form-group label-for="description">
                    <template #label>
                      <span>Patient<span style="color: tomato">*</span></span>
                    </template>
                    
                    <consultation-patient-select 
                      :partner="partnerData"
                      :value="form_data.patient_id"
                      @change="val => form_data.patient_id = val"
                    />

                    <b-form-invalid-feedback>
                      {{ validationContext.errors[0] }}
                    </b-form-invalid-feedback>
                  </b-form-group>
                </validation-provider>
              </b-col>
            </b-row>

            <!-- Form Actions -->
            <div class="d-flex mt-2">
              <b-button
                v-ripple.400="'rgba(255, 255, 255, 0.15)'"
                variant="primary"
                class="mr-2"
                type="button"
                :disabled="invalid || disableForm"
                @click="onCreateOrUpdate(true)"
              >
                Save & Proceed
              </b-button>

              <b-button
                v-ripple.400="'rgba(255, 255, 255, 0.15)'"
                variant="outline-primary"
                class="mr-2"
                type="submit"
                :disabled="invalid || disableForm"
              >
                Save & Exit
              </b-button>

              <b-button
                v-ripple.400="'rgba(186, 191, 199, 0.15)'"
                type="button"
                variant="outline-danger"
                @click="hide"
              >
                Cancel
              </b-button>
            </div>
          </b-form>
        </validation-observer>
      </b-overlay>
    </template>
  </b-modal>
</template>

<script>
import {
  BOverlay, BSidebar, BForm, BFormGroup, BFormInput, 
  BFormTextarea, BFormInvalidFeedback, BButton, BFormSelect,
  BAlert, BCardText, BModal, BRow, BCol, BFormDatepicker
} from 'bootstrap-vue'

import PartnerSelect from "@core/components/shared/admin/selects/PartnerSelect.vue"
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import ConsultationPatientSelect from "@core/components/shared/admin/selects/ConsultationPatientSelect.vue"

import { required } from '@validations'
import { get, cloneDeep } from 'lodash';
import { ValidationProvider, ValidationObserver } from 'vee-validate'

import vSelect from 'vue-select'
import Ripple from 'vue-ripple-directive'

const rawData = {
  patient_id: '',
}

export default {
  components: {
    BRow,
    BCol,
    BForm,
    BModal,
    BAlert,
    BButton,
    vSelect,
    BOverlay,
    BSidebar,
    BCardText,
    BFormGroup,
    BFormInput,
    BFormSelect,
    BFormTextarea,
    BFormDatepicker,
    BFormInvalidFeedback,

    // Form Validation
    ValidationProvider,
    ValidationObserver,
    PartnerSelect,
    ConsultationPatientSelect
  },
  directives: {
    Ripple,
  },
  model: {
    prop: 'isAddActive',
    event: 'update:is-add-active',
  },
  props: {
    resource: {
      type: String,
      required: true
    },
    isAddActive: {
      type: Boolean,
      required: true,
    },
    modalTitle: {
      type: String,
      default: 'Record'
    }
  },
  data() {
    return {
      required,
      loading: false,
      errorMessage: "",
      partnerData: "",

      answerTypes: [
        {
          label: 'Checkbox',
          value: 'checkbox'
        },
        {
          label: 'Text',
          value: 'text'
        },
        {
          label: 'Select',
          value: 'select'
        }
      ],
      commonComplaints: [],
      form_data: cloneDeep(rawData),
    }
  },
  computed: {
    disableForm(){
      return !this.can(`${this.resource}:CanAddNew`)
    },
  },
  methods: {
    async onCreateOrUpdate(save_and_proceed = false){
      try {
        this.loading = true;
        this.errorMessage = "";

        const success = await this.$refs.update_modal_form.validate();
        if (!success){
          return;
        }

        if (!this.can(`${this.resource}:CanAddNew`)) {
          return
        }

        this.$emit('create', {
          payload: this.form_data,
          save_and_proceed
        });
      } catch (error) {
        const error_message = get(error, "response.data.message") || error.message
        this.errorMessage = error_message;

        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Error!',
            icon: 'AlertTriangleIcon',
            variant: 'danger',
            text: error_message
          },
        });
      } finally {
        this.loading = false
      }
    },
    resetForm() {
      this.form_data = cloneDeep(rawData)
      if (this.$refs.update_modal_form) {
        this.$refs.update_modal_form.reset()
      }
    }
  }
}
</script>

<style lang="scss">
@import '@core/scss/vue/libs/vue-select.scss';
</style>
